import { MouseEvent, useEffect, useRef } from 'react';
import cn from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/router';

import LinkButton from '_components/UI/LinkButton';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import {
  PTT_TYPE_BY_SIZE,
  trackProductTakeoverTileClick,
} from '_utils/GA4/trackProductTakeoverTileEvent';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { useLocale } from '@hooks/useLocale';
import { useTranslation } from '@i18n/next-i18next-shim';
import { addLocaleToUrl } from '@utils/url';
import {
  InteractionLocation,
  PromotionEventName,
  trackPromotionEvent,
  PTTTrackPromotionEventProps,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';
import {
  TakeoverTilePTT,
  TakeoverTileSize,
  TakeoverTileStyle,
} from '@adapters/prismic/types/plp';

import styles from './ProductTakeoverTile.module.scss';

export interface ProductTakeoverTileProps {
  ptt: TakeoverTilePTT;
  isPriority?: boolean;
}

/**
 * ED-209 — the redesigned Product Takeover Tile (PTT).
 *
 * A promotional tile that occupies one or two product-card slots in the PLP
 * grid. Two visual styles from the Figma component set:
 *  - `promo_image`  image on top, content block beneath on a light surface
 *  - `full_bleed`   image fills the tile, content overlaid on a bottom scrim
 *
 * Accessibility: the whole tile is a single `<a>`. The headline, body and CTA
 * are inert descendants, so there is exactly one tab stop and the focus ring
 * wraps the entire tile. The CTA reuses `LinkButton` with `as="span"` because
 * a nested `<a>` would be invalid and would add a second tab stop.
 */
const ProductTakeoverTile = ({
  ptt,
  isPriority = false,
}: ProductTakeoverTileProps) => {
  const locale = useLocale();
  const { asPath } = useRouter();
  const { t } = useTranslation('plp');
  const tileRef = useRef<HTMLElement>(null);
  const isVisible = useObjectVisibility(tileRef);

  const { style, size, image, headline, body, link } = ptt.product_tile;
  const isFullBleed = style === TakeoverTileStyle.fullBleed;
  const isWide = size === TakeoverTileSize.two;

  const href = link?.url ? addLocaleToUrl(link.url, locale) : undefined;

  // Built at event time rather than render time: `window.location` and
  // `determinePageType()` are unavailable during SSR.
  const buildAnalytics = (): Omit<
    PTTTrackPromotionEventProps,
    'eventName'
  > => ({
    page: {
      path: window.location.pathname,
      page_type: determinePageType(),
    },
    interaction: {
      coop_brand: ptt.product_tile['co-op_brand_attribute'] || null,
      interaction_location: InteractionLocation.PTT,
    },
    ecommerce: {
      creative_name: `link,${link?.url || ''}`,
      creative_slot: buildCreativeSlotName({
        sliceIndex: ptt.position,
        interactionLocation: InteractionLocation.PTT,
        itemIndex: ptt.treatment,
      }),
      promotion_id: headline || '',
      promotion_name: link?.text || '',
      isVideo: false,
    },
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && isVisible) {
      trackPromotionEvent({
        eventName: PromotionEventName.viewPromotion,
        ...buildAnalytics(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, ptt]);

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    trackPromotionEvent({
      eventName: PromotionEventName.selectPromotion,
      ...buildAnalytics(),
    });

    const data = {
      // Query string dropped so the tile reports the same page as the other
      // PLP surfaces regardless of the facets/pagination in play.
      interactionLocation: asPath.split('?')[0],
      linkUrl: href || '',
      linkText: link?.text || '',
      coOpBrand: ptt.product_tile['co-op_brand_attribute'] || null,
      type: PTT_TYPE_BY_SIZE[size],
    };

    // A modified click keeps this page alive, so the hit is not at risk and
    // navigation must not be hijacked. Same when there is no href to defer to.
    if (isModifiedClick(e) || !href) {
      trackProductTakeoverTileClick({ data });
      return;
    }

    e.preventDefault();
    trackProductTakeoverTileClick({
      data,
      eventCallback: () => {
        window.location.href = href;
      },
    });
  };

  const ariaLabel = body
    ? t('ptt.ariaLabel', { headline, body })
    : t('ptt.ariaLabelNoBody', { headline });

  // Mirrors the column fractions in Hits.module.scss so the browser downloads
  // an appropriately sized image at each breakpoint.
  const sizes = isWide
    ? '(min-width: 1128px) calc(50vw - 48px), (min-width: 1000px) calc(67vw - 48px), (min-width: 767px) calc(100vw - 48px), (min-width: 600px) calc(67vw - 48px), calc(100vw - 48px)'
    : '(min-width: 1128px) calc(25vw - 48px), (min-width: 1000px) calc(34vw - 48px), (min-width: 767px) calc(50vw - 48px), (min-width: 600px) calc(34vw - 48px), (min-width: 374px) calc(50vw - 48px), calc(100vw - 48px)';

  const hasImage = Boolean(image?.url);

  const imageEl = hasImage ? (
    <Image
      src={image?.url as string}
      alt={image?.alt ?? ''}
      fill
      sizes={sizes}
      priority={isPriority}
      className={styles.image}
    />
  ) : null;

  const content = (
    <>
      <div className={styles.text}>
        {headline && <p className={styles.headline}>{headline}</p>}
        {body && <p className={styles.body}>{body}</p>}
      </div>
      {link?.text && (
        <LinkButton
          as="span"
          variant={isFullBleed ? 'white' : 'black'}
          className={styles.cta}
        >
          {link.text}
        </LinkButton>
      )}
    </>
  );

  return (
    <article
      ref={tileRef}
      className={cn(styles.tile, {
        [styles.fullBleed]: isFullBleed,
        [styles.promoImage]: !isFullBleed,
        [styles.wide]: isWide,
      })}
      data-testid="product-takeover-tile"
    >
      <a
        href={href}
        aria-label={ariaLabel}
        className={styles.link}
        onClick={handleClick}
      >
        <div className={styles.imageWrapper}>{imageEl}</div>
        {isFullBleed && <span className={styles.scrim} aria-hidden="true" />}
        <div className={styles.content}>{content}</div>
      </a>
    </article>
  );
};

export default ProductTakeoverTile;
